import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { UserAvatar } from '@/components/ui/user-avatar';
import { MentionTextarea } from './MentionTextarea';
import { useTaskComments } from '@/hooks/useTaskComments';
import { useStableAuth } from '@/hooks/useStableAuth';
import { MessageSquare, Send } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface TaskCommentsProps {
  taskId: string;
}

export const TaskComments = ({ taskId }: TaskCommentsProps) => {
  const { userId } = useStableAuth();
  const { comments, loading, addComment } = useTaskComments(taskId);
  const [newComment, setNewComment] = useState('');
  const [mentions, setMentions] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      await addComment(newComment, mentions);
      setNewComment('');
      setMentions([]);
    } catch (error) {
      // Error handled in hook
    } finally {
      setSubmitting(false);
    }
  };

  // Renderizar comentários com menções destacadas
  const renderCommentWithMentions = (content: string) => {
    const mentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
    const parts: (string | JSX.Element)[] = [];
    let lastIndex = 0;
    let match;
    let key = 0;

    while ((match = mentionRegex.exec(content)) !== null) {
      // Texto antes da menção
      if (match.index > lastIndex) {
        parts.push(content.substring(lastIndex, match.index));
      }

      // Menção com estilo
      parts.push(
        <span key={key++} className="text-primary font-medium">
          @{match[1]}
        </span>
      );

      lastIndex = match.index + match[0].length;
    }

    // Texto após última menção
    if (lastIndex < content.length) {
      parts.push(content.substring(lastIndex));
    }

    return parts.length > 0 ? parts : content;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <MessageSquare className="h-4 w-4" />
        <h4 className="text-sm font-medium">Comentários</h4>
        <span className="text-xs text-muted-foreground">({comments.length})</span>
      </div>

      <div className="space-y-3">
        {loading ? (
          <div className="text-sm text-muted-foreground">Carregando comentários...</div>
        ) : comments.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-4">
            Nenhum comentário ainda. Seja o primeiro!
          </div>
        ) : (
          comments.map(comment => (
            <div key={comment.id} className="flex gap-3 p-3 rounded-lg bg-muted/50">
              <UserAvatar
                avatarUrl={comment.user?.avatar_url}
                name={comment.user?.full_name}
                size="sm"
              />
              <div className="flex-1 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{comment.user?.full_name}</span>
                  <span className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(comment.created_at), {
                      addSuffix: true,
                      locale: ptBR
                    })}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {renderCommentWithMentions(comment.content)}
                </p>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="space-y-2">
        <MentionTextarea
          value={newComment}
          onChange={(value, detectedMentions) => {
            setNewComment(value);
            setMentions(detectedMentions);
          }}
          placeholder="Adicione um comentário... Use @ para mencionar alguém"
          rows={3}
          disabled={submitting}
        />
        <div className="flex justify-end">
          <Button
            onClick={handleSubmit}
            disabled={!newComment.trim() || submitting}
            size="sm"
          >
            <Send className="h-4 w-4 mr-2" />
            {submitting ? 'Enviando...' : 'Enviar'}
          </Button>
        </div>
      </div>
    </div>
  );
};
